import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  BarChartOutlined,
  UserOutlined,
  InfoOutlined,
  AuditOutlined,
  DiffOutlined,
  ContactsOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons';
import { Menu } from 'antd';
import { menuInboxBadgeCounterVar } from '../../graphql/cache';
import {
  DashBoardIcon,
  CallCenterIcon,
  InvoicesIcon,
  getPermission,
  AVAILABLE_ROLES,
  useTranslation,
} from '../../utils';
import CustomMenuBadge from './CustomMenuBadge';
import { ICustomMenuBadgeProps } from './CustomMenuBadge/CustomMenuBadge';
import ExpandIcon from './ExpandIcon/ExpandIcon';

interface ISidebarLinksChild {
  nameId: string;
  icon: JSX.Element;
  path: string;
  basePath?: string;
}

interface ISidebarLinks {
  nameId: string;
  icon: JSX.Element;
  path?: string;
  hasPermission: boolean | ((rules: string[]) => boolean);
  queryBasedPermission?: string;
  children?: ISidebarLinksChild[];
  basePath?: string;
}

export const getSiderbarLinks = (): ISidebarLinks[] => {
  return [
    {
      nameId: 'dashboard',
      icon: <DashBoardIcon />,
      path: '/',
      hasPermission: true,
    },
    {
      nameId: 'callCenter',
      icon: <CallCenterIcon />,
      hasPermission: true,
      path: '/messages/call-center',
      basePath: '/messages/call-center',
    },
    {
      nameId: 'invoices',
      icon: <InvoicesIcon />,
      path: '/invoices',
      hasPermission: getPermission([AVAILABLE_ROLES.SUPERADMIN, AVAILABLE_ROLES.ADMIN]),
      basePath: '/invoices',
    },
    {
      nameId: 'reports',
      icon: <BarChartOutlined />,
      path: '/reports/incoming',
      hasPermission: getPermission([AVAILABLE_ROLES.SUPERADMIN, AVAILABLE_ROLES.ADMIN]),
      basePath: '/reports',
    },
    {
      nameId: 'customerDetails',
      icon: <UserOutlined />,
      hasPermission: true,
      children: [
        {
          nameId: 'information',
          icon: <InfoOutlined />,
          path: '/customer/info',
          basePath: '/customer/info',
        },
        {
          nameId: 'instructions',
          icon: <AuditOutlined />,
          path: '/customer/instructions/fixed',
          basePath: '/customer/instructions',
        },
        {
          nameId: 'attachments',
          icon: <DiffOutlined />,
          path: '/customer/attachments',
          basePath: '/customer/attachments',
        },
        {
          nameId: 'crmContacts',
          icon: <ContactsOutlined />,
          path: '/customer/crmcontacts',
          basePath: '/customer/crmcontacts',
        },
      ],
    },
    {
      nameId: 'users',
      icon: <UsergroupAddOutlined />,
      path: '/settings/users',
      hasPermission: getPermission([AVAILABLE_ROLES.SUPERADMIN, AVAILABLE_ROLES.ADMIN]),
      basePath: '/settings/users',
    },
  ];
};

interface ISidebarMenuProps {
  collapsed: boolean;
}

const SidebarMenu: React.FC<ISidebarMenuProps> = ({ collapsed }) => {
  const translate = useTranslation();
  const location = useLocation();
  const [queryBasedPermissions] = useState<string[]>([]);

  const defaultOpenSubmenu = getSiderbarLinks().find((link) => {
    return link.children?.find((child) => {
      return child.path === location.pathname;
    });
  });

  const getMenuItemClassName = (basePath: string | undefined): string => {
    return basePath !== undefined && location.pathname.startsWith(basePath)
      ? 'ant-menu-item-selected'
      : '';
  };

  const menuItems = getSiderbarLinks()
    .filter(({ hasPermission, queryBasedPermission }) => {
      if (!!queryBasedPermission && !queryBasedPermissions.includes(queryBasedPermission)) {
        return false;
      }
      return hasPermission;
    })
    .map(({ nameId, icon, children, path, basePath }) => {
      if (children) {
        return {
          key: nameId,
          icon: <div>{icon}</div>,
          label: (
            <div className="sidebar-link-container">
              <span>{translate(nameId)}</span>
            </div>
          ),
          children: children.map((child: ISidebarLinksChild) => {
            return {
              key: child.path,
              label: (
                <div className="sidebar-link-container">
                  <NavLink to={child.path}>
                    <div className="menu-item">
                      {child.icon} {translate(child.nameId)}{' '}
                    </div>
                  </NavLink>
                </div>
              ),
              className: getMenuItemClassName(child.basePath),
            };
          }),
        };
      }

      if (!path) {
        return null;
      }

      const pagesWithBadges: Array<keyof typeof menuInboxBadgeCounterVar | string> = ['callCenter'];

      return {
        key: path,
        icon: (
          <NavLink to={path} className="sidebar-link">
            {icon}
          </NavLink>
        ),
        label: (
          <div className="sidebar-link-container">
            <NavLink to={path} className="sidebar-link">
              <span className="sidebar-text">{translate(nameId)}</span>
              {pagesWithBadges.includes(nameId) && (
                <CustomMenuBadge pageName={nameId as ICustomMenuBadgeProps['pageName']} />
              )}
            </NavLink>
          </div>
        ),
        className: getMenuItemClassName(basePath),
      };
    })
    .filter((item) => {
      return item !== null;
    }) as Array<{
    key: string;
    icon: JSX.Element;
    label: JSX.Element;
    className?: string;
    children?: Array<{
      key: string;
      label: JSX.Element;
      className: string;
    }>;
  }>;

  return (
    <Menu
      expandIcon={ExpandIcon}
      className={collapsed ? 'collapsed-menu' : ''}
      mode="inline"
      selectedKeys={[location.pathname]}
      defaultOpenKeys={[defaultOpenSubmenu?.nameId || '']}
      items={menuItems}
    />
  );
};

export default SidebarMenu;
